import type { ProjectResponse } from '@/backend/types'
import type { Property } from '@/modules/Project/Properties/types'
import { serializeProperty, serializeView } from '@/modules/Project/serializer'
import type { View } from '@/modules/Project/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type ProjectInList = Omit<
  Project,
  'views' | 'properties' | 'autoRecalculations' | 'mainViewId'
>

export type Project = {
  autoRecalculations: ProjectResponse['auto_recalculations']
  coverImageUrls: ProjectResponse['cover_image_urls']
  createdAt: ProjectResponse['created_at']
  description: ProjectResponse['description']
  id: ProjectResponse['id']
  mainViewId: ProjectResponse['main_view_id']
  name: ProjectResponse['name']
  parentFolderId: ProjectResponse['parent_folder_id']
  parentProperty?: Property
  properties: Property[]
  updatedAt: ProjectResponse['updated_at']
  views: View[]
  workspaceId: ProjectResponse['workspace_id']
}

export const serializeProject = (project: ProjectResponse): Project => {
  return {
    id: project.id,
    name: project.name,
    workspaceId: project.workspace_id,
    parentProperty: project.parent_property
      ? serializeProperty(project.parent_property)
      : undefined,
    parentFolderId: project.parent_folder_id,
    coverImageUrls: project.cover_image_urls,
    views: project.views.map(serializeView),
    updatedAt: project.updated_at,
    createdAt: project.created_at,
    properties: project.properties.map(serializeProperty),
    description: project.description,
    autoRecalculations: project.auto_recalculations,
    mainViewId: project.main_view_id,
  }
}

export const useProjects = defineStore('projects', () => {
  const addProject = (project: ProjectInList) => {
    projectMap.value[project.id] = project
  }

  const loadingState = ref<'idle' | 'loading' | 'loaded' | 'errored'>('idle')
  const projectMap = ref<Record<ProjectInList['id'], ProjectInList | undefined>>({})
  const projects = computed<ProjectInList[]>(() =>
    Object.values(projectMap.value).filter((p): p is ProjectInList => !!p),
  )

  const getProjectById = (id: string) => projectMap.value[id]

  const setProjects = (p: Project[]) => {
    projectMap.value = Object.fromEntries(p.map((p) => [p.id, p]))
  }

  const addProjects = (p: ProjectInList[]) => {
    p.forEach((p) => {
      projectMap.value[p.id] = p
    })
  }

  const updateProject = (id: string, data: Partial<ProjectInList>) => {
    const project = projectMap.value[id]
    if (!project) {
      return
    }
    projectMap.value[id] = { ...project, ...data }
  }

  const removeProject = (id: string) => {
    projectMap.value[id] = undefined
  }

  const mainProjects = computed(() =>
    projects.value.filter((p) => p && p.parentProperty === undefined),
  )

  const setLoadingState = (state: 'idle' | 'loading' | 'loaded' | 'errored') => {
    loadingState.value = state
  }

  return {
    projectMap,
    loadingState,
    addProject,
    addProjects,
    getProjectById,
    updateProject,
    removeProject,
    projects,
    setProjects,
    mainProjects,
    setLoadingState,
  }
})
